import { useLanguage } from "@/context/LanguageContext";
import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { isMobile } from "react-device-detect";

interface TimelineItem {
  id: number;
  year: string;
  title: string;
  company: string;
  description: string;
}

export const ExperienceSection = () => {
  const { t } = useLanguage();
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, {
    once: true,
    amount: isMobile ? 0.1 : 0.2,
  });

  // Sample timeline data (replace with real data)
  const timelineData: TimelineItem[] = [
    {
      id: 1,
      year: "2019 - " + t("experience.present"),
      title: "eCommerce UX/UI Operations Developer",
      company: "Nestlé Nespresso SA",
      description: t("experience.timeline.1.description"),
    },
    {
      id: 2,
      year: "2019",
      title: "Senior Consultant",
      company: "HCL Technologies",
      description: t("experience.timeline.2.description"),
    },
    {
      id: 3,
      year: "2017 - 2018",
      title: "Senior Associate Experience Technology",
      company: "SapientRazorfish",
      description: t("experience.timeline.3.description"),
    },
    {
      id: 4,
      year: "2016 - 2017",
      title: "Senior Interactive Developer",
      company: "SapientNitro",
      description: t("experience.timeline.4.description"),
    },
    {
      id: 5,
      year: "2014 - 2016",
      title: "Associate Projects",
      company: "Cognizant Technology Solutions",
      description: t("experience.timeline.5.description"),
    },
    {
      id: 6,
      year: "2013 - 2014",
      title: "Senior System Engineer",
      company: "Infosys",
      description: t("experience.timeline.6.description"),
    },
    {
      id: 7,
      year: "2011 - 2013",
      title: "System Engineer",
      company: "Infosys",
      description: t("experience.timeline.7.description"),
    },
    {
      id: 8,
      year: "2011-2011",
      title: "Cyber Security Expert Trainee",
      company: "Techdefence Pvt. Ltd",
      description: t("experience.timeline.8.description"),
    },
  ];

  // Define animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: isMobile ? 0.1 : 0.2, // Less stagger on mobile,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: isMobile ? 10 : 20 }, // Less y-offset on mobile
    show: { opacity: 1, y: 0, transition: { duration: isMobile ? 0.4 : 0.6 } }, // Faster animation on mobile
  };

  const lineVariants = {
    hidden: { scaleY: 0, originY: 0 },
    show: {
      scaleY: 1,
      transition: {
        duration: isMobile ? 0.6 : 0.8, // Shorter on mobile
        ease: isMobile ? "easeIn" : "easeInOut", // simpler easing
      },
    },
  };

  const dotVariants = {
    hidden: { scale: 0, opacity: 0 },
    show: (i: number) => ({
      scale: 1,
      opacity: 1,
      transition: {
        delay: isMobile ? 0.2 + i * 0.05 : 0.3 + i * 0.1, // Less delay on mobile
        duration: 0.4,
        type: isMobile ? "easeOut" : "spring", // simpler easing
        stiffness: 200,
      },
    }),
  };

  return (
    <section
      id="experience"
      ref={sectionRef}
      className="py-20 px-6 md:px-12 lg:px-24 relative"
    >
      <div className="max-w-5xl mx-auto">
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.6 }}
          className="text-3xl font-bold mb-16 text-center"
        >
          {t("experience.title")}
        </motion.h2>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "show" : "hidden"}
          className="relative"
        >
          {/* Vertical timeline line */}
          <motion.div
            className="absolute left-0 md:left-1/2 transform md:-translate-x-1/2 top-0 bottom-0 w-1 bg-accent/30 z-0"
            variants={lineVariants}
          ></motion.div>

          {timelineData.map((item, index) => (
            <motion.div
              key={item.id}
              variants={itemVariants}
              className={`relative z-10 mb-16 md:mb-24 flex flex-col ${
                index % 2 === 0 ? "md:flex-row" : "md:flex-row-reverse"
              }`}
            >
              {/* Timeline dot */}
              <motion.div
                className="absolute left-0 md:left-1/2 transform -translate-x-1/2 w-4 h-4 rounded-full bg-accent z-20"
                variants={dotVariants}
                custom={index}
              ></motion.div>

              {/* Year */}
              <motion.div
                className={`w-full md:w-1/2 ${
                  index % 2 === 0 ? "md:pr-12 md:text-right" : "md:pl-12"
                }`}
                whileHover={{ scale: 1.03, transition: { duration: 0.2 } }}
              >
                <span className="inline-block py-1.5 px-3 bg-accent/10 text-accent rounded-full mb-4">
                  {item.year}
                </span>
              </motion.div>

              {/* Content */}
              <motion.div
                className={`w-full md:w-1/2 pl-6 md:pl-0 ${
                  index % 2 === 0 ? "md:pl-12" : "md:pr-12 md:text-left"
                }`}
                whileHover={{ scale: 1.03, transition: { duration: 0.2 } }}
              >
                <div className="p-6 bg-secondary/40 rounded-xl backdrop-blur-sm">
                  <h3 className="text-xl font-bold text-foreground">
                    {item.title}
                  </h3>
                  <h4 className="text-lg font-medium text-accent mb-2">
                    {item.company}
                  </h4>
                  <p className="text-muted-foreground mb-4">
                    {item.description}
                  </p>
                </div>
              </motion.div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};
